import { isDefined } from "@clipboard-health/util-ts";
import { useDebouncedValue } from "@src/appV2/lib/utils/useDebouncedValue";
import { type Agent, useSearchAgents } from "@src/appV2/User/api/useSearchAgents";
import { type Facility, useSearchFacilities } from "@src/appV2/User/api/useSearchFacilities";
import { useMemo } from "react";

import { useGetWorkerShifts, type WorkerShift } from "../api/useGetWorkerShifts";
import { isValidShiftId, type ShiftSearchResult, useSearchShift } from "../api/useSearchShift";
import { type ShiftAction } from "../components/ShiftActionItem";

const SEARCH_LIMIT = 5;

export type SearchResultItem =
  | { type: "agent"; data: Agent; expanded?: boolean }
  | { type: "facility"; data: Facility }
  | { type: "shift"; data: ShiftSearchResult; expanded: boolean }
  | { type: "shift-action"; data: ShiftSearchResult; action: ShiftAction }
  | { type: "agent-shift"; data: WorkerShift; workerId: string };

interface UseOmnisearchResultsParams {
  searchInput: string;
  expandedShiftId: string | null;
  expandedWorkerId: string | null;
}

export function useOmnisearchResults(params: UseOmnisearchResultsParams) {
  const { searchInput, expandedShiftId, expandedWorkerId } = params;
  const [debouncedSearchInput] = useDebouncedValue(searchInput, 200);

  const { data: agentsData, isLoading: isLoadingAgents } = useSearchAgents({
    searchInput: debouncedSearchInput,
    limit: SEARCH_LIMIT,
  });

  const { data: facilitiesData, isLoading: isLoadingFacilities } = useSearchFacilities({
    searchInput: debouncedSearchInput,
    limit: SEARCH_LIMIT,
  });

  const { data: shiftData, isLoading: isLoadingShift } = useSearchShift({
    shiftId: debouncedSearchInput,
  });

  const {
    shifts: workerShifts,
    isLoading: isLoadingWorkerShifts,
    hasShifts: workerHasShifts,
  } = useGetWorkerShifts({
    workerId: expandedWorkerId ?? undefined,
    enabled: isDefined(expandedWorkerId),
  });

  const { agents, facilities, shift, allResults } = useMemo(() => {
    const agentsList = agentsData?.pages.flatMap((page) => page.list) ?? [];
    const facilitiesList = facilitiesData?.pages.flatMap((page) => page.list) ?? [];
    const shiftResult = isValidShiftId(debouncedSearchInput) ? shiftData ?? null : null;

    const results: SearchResultItem[] = [];

    agentsList.forEach((agent) => {
      const isExpanded = expandedWorkerId === agent.userId;
      results.push({ type: "agent", data: agent, expanded: isExpanded });

      if (isExpanded) {
        workerShifts.forEach((workerShift) => {
          results.push({ type: "agent-shift", data: workerShift, workerId: agent.userId });
        });
      }
    });

    facilitiesList.forEach((facility) => {
      results.push({ type: "facility", data: facility });
    });

    if (isDefined(shiftResult)) {
      const isExpanded = expandedShiftId === shiftResult.id;
      results.push({ type: "shift", data: shiftResult, expanded: isExpanded });

      if (isExpanded) {
        if (isDefined(shiftResult.agent?.userId)) {
          results.push({ type: "shift-action", data: shiftResult, action: "worker" });
        }
        if (isDefined(shiftResult.facility?.userId)) {
          results.push({ type: "shift-action", data: shiftResult, action: "workplace" });
        }
      }
    }

    return {
      agents: agentsList,
      facilities: facilitiesList,
      shift: shiftResult,
      allResults: results,
    };
  }, [
    agentsData,
    facilitiesData,
    shiftData,
    debouncedSearchInput,
    expandedShiftId,
    expandedWorkerId,
    workerShifts,
  ]);

  const isLoading =
    isLoadingAgents ||
    isLoadingFacilities ||
    (isValidShiftId(debouncedSearchInput) && isLoadingShift);
  const hasResults = agents.length > 0 || facilities.length > 0 || isDefined(shift);
  const hasSearchInput = debouncedSearchInput.length > 0;

  return {
    agents,
    facilities,
    shift,
    shiftData,
    workerShifts,
    allResults,
    isLoading,
    isLoadingWorkerShifts,
    workerHasShifts,
    hasResults,
    hasSearchInput,
    debouncedSearchInput,
  };
}
